<script>
/* global axios */
// Diálogo de encaminhar mensagem: escolhe inbox + destinatários e envia o
// conteúdo numa nova conversa. O campo de destinatário BUSCA contatos ao digitar
// (autocomplete) e permite até MAX_RECIPIENTS destinatários (chips). Também aceita
// telefone/email digitado direto (destinatário manual). Independe de provedor.
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';

const MAX_RECIPIENTS = 10;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^\+?\d[\d\s()-]{6,}$/;

export default {
  components: { NextButton, Avatar },
  props: {
    message: { type: Object, required: true },
  },
  emits: ['close'],
  data() {
    return {
      maxRecipients: MAX_RECIPIENTS,
      selectedInboxIds: [],
      recipients: [], // { key, label, identifier, type: 'contact' | 'manual', thumbnail }
      query: '',
      results: [],
      searching: false,
      showResults: false,
      content: '',
      saving: false,
      searchTimer: null,
    };
  },
  computed: {
    ...mapGetters({
      inboxes: 'inboxes/getInboxes',
      accountId: 'getCurrentAccountId',
    }),
    isFull() {
      return this.recipients.length >= this.maxRecipients;
    },
    // Sugere adicionar o texto digitado como destinatário manual (telefone/email)
    // quando ele é válido e ainda não está na lista.
    manualSuggestion() {
      const q = this.query.trim();
      if (!q) return null;
      const isEmail = EMAIL_RE.test(q);
      const isPhone = PHONE_RE.test(q);
      if (!isEmail && !isPhone) return null;
      const identifier = isEmail ? q.toLowerCase() : q.replace(/\D/g, '');
      if (this.recipients.some(r => r.identifier === identifier)) return null;
      return { identifier, label: q, type: 'manual' };
    },
    hasAttachments() {
      return !!this.message?.attachments?.length;
    },
    canSubmit() {
      return (
        this.selectedInboxIds.length &&
        this.recipients.length &&
        (this.content.trim() || this.hasAttachments)
      );
    },
  },
  watch: {
    query() {
      this.debouncedSearch();
    },
  },
  async mounted() {
    this.content = this.message?.content || '';
    // garante que a lista de caixas está carregada (senão fica vazia)
    if (!this.inboxes.length) {
      try {
        await this.$store.dispatch('inboxes/get');
      } catch (e) {
        // mantém o que tiver
      }
    }
  },
  beforeUnmount() {
    if (this.searchTimer) clearTimeout(this.searchTimer);
  },
  methods: {
    toggleInbox(id) {
      const i = this.selectedInboxIds.indexOf(id);
      if (i === -1) this.selectedInboxIds.push(id);
      else this.selectedInboxIds.splice(i, 1);
    },
    debouncedSearch() {
      if (this.searchTimer) clearTimeout(this.searchTimer);
      const q = this.query.trim();
      this.showResults = true;
      if (!q) {
        this.results = [];
        this.searching = false;
        return;
      }
      this.searching = true;
      this.searchTimer = setTimeout(() => this.runSearch(q), 300);
    },
    async runSearch(q) {
      try {
        const { data } = await axios.get(
          `/api/v1/accounts/${this.accountId}/contacts/search`,
          { params: { q, page: 1, sort: 'name' } }
        );
        const payload = data?.payload || [];
        // só contatos com telefone ou email (precisamos de um identificador)
        this.results = payload
          .filter(c => c.phone_number || c.email)
          .map(c => ({
            key: `c-${c.id}`,
            label: c.name || c.phone_number || c.email,
            sub: c.phone_number || c.email,
            identifier: c.phone_number
              ? c.phone_number.replace(/\D/g, '')
              : c.email.toLowerCase(),
            type: 'contact',
            thumbnail: c.thumbnail,
          }))
          .filter(
            r => !this.recipients.some(x => x.identifier === r.identifier)
          );
      } catch (e) {
        this.results = [];
      } finally {
        this.searching = false;
      }
    },
    addRecipient(r) {
      if (this.isFull) {
        useAlert(
          this.$t('CONVERSATION.FORWARD.MAX_REACHED', {
            max: this.maxRecipients,
          })
        );
        return;
      }
      if (this.recipients.some(x => x.identifier === r.identifier)) return;
      this.recipients.push({
        key: r.key || `m-${r.identifier}`,
        label: r.label,
        identifier: r.identifier,
        type: r.type,
        thumbnail: r.thumbnail,
      });
      this.query = '';
      this.results = [];
      this.showResults = false;
      this.$nextTick(() => this.$refs.searchInput?.focus());
    },
    addManual() {
      if (this.manualSuggestion) this.addRecipient(this.manualSuggestion);
    },
    onEnter() {
      if (this.results.length) this.addRecipient(this.results[0]);
      else if (this.manualSuggestion) this.addManual();
    },
    removeRecipient(key) {
      this.recipients = this.recipients.filter(r => r.key !== key);
    },
    hideResultsSoon() {
      // pequeno delay p/ permitir o clique num item antes de fechar
      setTimeout(() => {
        this.showResults = false;
      }, 150);
    },
    async submit() {
      if (!this.canSubmit) return;
      this.saving = true;
      try {
        const { data } = await axios.post(
          `/api/v1/accounts/${this.accountId}/kanban/forward_messages`,
          {
            inbox_ids: this.selectedInboxIds,
            contact_identifiers: this.recipients.map(r => r.identifier),
            content: this.content.trim(),
            message_id: this.message?.id,
          }
        );
        useAlert(
          this.$t('CONVERSATION.FORWARD.SUCCESS', {
            forwarded: data.forwarded,
            total: data.total,
          })
        );
        this.$emit('close');
      } catch (e) {
        useAlert(
          e?.response?.data?.error || this.$t('CONVERSATION.FORWARD.ERROR')
        );
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<template>
  <div
    class="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-n-alpha-black2"
    @click.self="$emit('close')"
  >
    <div class="w-[28rem] max-w-full p-6 bg-n-background rounded-xl shadow-xl">
      <h2 class="mb-4 text-base font-medium text-n-slate-12">
        {{ $t('CONVERSATION.FORWARD.TITLE') }}
      </h2>
      <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
        $t('CONVERSATION.FORWARD.INBOX')
      }}</label>
      <div
        class="mb-3 max-h-40 overflow-y-auto border rounded-lg border-n-weak divide-y divide-n-weak"
      >
        <label
          v-for="ib in inboxes"
          :key="ib.id"
          class="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer text-n-slate-12 hover:bg-n-alpha-1"
        >
          <input
            type="checkbox"
            :checked="selectedInboxIds.includes(ib.id)"
            @change="toggleInbox(ib.id)"
          />
          {{ ib.name }}
        </label>
        <p v-if="!inboxes.length" class="px-3 py-2 text-xs text-n-slate-10">
          —
        </p>
      </div>

      <div class="flex items-center justify-between mb-1">
        <label class="text-sm font-medium text-n-slate-12">{{
          $t('CONVERSATION.FORWARD.CONTACT')
        }}</label>
        <span class="text-xs text-n-slate-10">
          {{ recipients.length }}/{{ maxRecipients }}
        </span>
      </div>

      <!-- chips dos destinatários selecionados -->
      <div v-if="recipients.length" class="flex flex-wrap gap-1.5 mb-2">
        <span
          v-for="r in recipients"
          :key="r.key"
          class="inline-flex items-center gap-1 pl-2 pr-1 py-1 text-xs rounded-full bg-n-alpha-2 text-n-slate-12"
        >
          {{ r.label }}
          <button
            type="button"
            class="flex items-center justify-center w-4 h-4 rounded-full hover:bg-n-alpha-2 text-n-slate-11"
            @click="removeRecipient(r.key)"
          >
            <span class="i-lucide-x text-xs" />
          </button>
        </span>
      </div>

      <!-- busca -->
      <div class="relative mb-3">
        <input
          ref="searchInput"
          v-model="query"
          :disabled="isFull"
          :placeholder="
            isFull
              ? $t('CONVERSATION.FORWARD.MAX_REACHED', { max: maxRecipients })
              : $t('CONVERSATION.FORWARD.CONTACT_PLACEHOLDER')
          "
          class="reset-base w-full px-3 py-2 text-sm border rounded-lg border-n-weak bg-n-alpha-1 text-n-slate-12 disabled:opacity-60"
          @focus="showResults = true"
          @blur="hideResultsSoon"
          @keydown.enter.prevent="onEnter"
        />
        <div
          v-if="showResults && !isFull && (query.trim() || searching)"
          class="absolute z-10 w-full mt-1 overflow-y-auto bg-n-background border rounded-lg shadow-lg border-n-weak max-h-56"
        >
          <div v-if="searching" class="px-3 py-2 text-xs text-n-slate-10">
            {{ $t('CONVERSATION.FORWARD.SEARCHING') }}
          </div>
          <template v-else>
            <button
              v-for="r in results"
              :key="r.key"
              type="button"
              class="flex items-center w-full gap-2 px-3 py-2 text-left cursor-pointer hover:bg-n-alpha-1"
              @mousedown.prevent="addRecipient(r)"
            >
              <Avatar
                :src="r.thumbnail"
                :name="r.label"
                :size="24"
                rounded-full
              />
              <span class="flex flex-col min-w-0">
                <span class="text-sm truncate text-n-slate-12">{{
                  r.label
                }}</span>
                <span class="text-xs truncate text-n-slate-10">{{
                  r.sub
                }}</span>
              </span>
            </button>
            <button
              v-if="manualSuggestion"
              type="button"
              class="flex items-center w-full gap-2 px-3 py-2 text-left cursor-pointer hover:bg-n-alpha-1 border-t border-n-weak"
              @mousedown.prevent="addManual"
            >
              <span class="i-lucide-plus text-n-slate-11" />
              <span class="text-sm text-n-slate-12">
                {{
                  $t('CONVERSATION.FORWARD.ADD_MANUAL', {
                    value: manualSuggestion.label,
                  })
                }}
              </span>
            </button>
            <div
              v-if="!results.length && !manualSuggestion && query.trim()"
              class="px-3 py-2 text-xs text-n-slate-10"
            >
              {{ $t('CONVERSATION.FORWARD.NO_RESULTS') }}
            </div>
          </template>
        </div>
      </div>

      <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
        $t('CONVERSATION.FORWARD.MESSAGE')
      }}</label>
      <textarea
        v-model="content"
        rows="4"
        class="w-full px-3 py-2 text-sm border rounded-lg resize-none border-n-weak bg-n-alpha-1 text-n-slate-12"
      />
      <div class="flex justify-end gap-2 mt-4">
        <NextButton
          slate
          faded
          sm
          :label="$t('CONVERSATION.FORWARD.CANCEL')"
          @click="$emit('close')"
        />
        <NextButton
          blue
          sm
          :disabled="!canSubmit"
          :is-loading="saving"
          :label="$t('CONVERSATION.FORWARD.SUBMIT')"
          @click="submit"
        />
      </div>
    </div>
  </div>
</template>
